/**
 * ProjectAgentSessionsPanel (AGENT-STATUS-1)
 *
 * "Agents on this project": the project's open work sessions, live over the bridge
 * socket. One row per run: agent, task, status, since when (or "No update since ..."
 * when it has gone quiet), and the backlog item it is linked to, if any.
 */

import { useBridge } from '../../layout/MainLayout';
import { useProjectWorkSessions } from '../../../hooks/useProjectWorkSessions';
import { formatSessionTime, quietNote, SESSION_STATE_STYLE } from '../../agents/workSessionFormat';

interface ProjectAgentSessionsPanelProps {
  projectId: string;
}

export const NO_AGENT_ON_PROJECT = 'No agent is working on this project right now.';

export function ProjectAgentSessionsPanel({ projectId }: ProjectAgentSessionsPanelProps) {
  const { socket } = useBridge();
  const { sessions, isLoading, error } = useProjectWorkSessions(projectId, socket);

  return (
    <section
      className="bg-white border border-zinc-200 rounded-lg p-4 mb-4 dark:bg-zinc-900 dark:border-zinc-800"
      aria-labelledby="project-agents-heading"
      data-testid="project-agent-sessions"
    >
      <div className="flex items-center justify-between mb-3">
        <h3 id="project-agents-heading" className="text-sm font-semibold uppercase tracking-wider text-zinc-500">
          Agents on this project
        </h3>
        {sessions.length > 0 && (
          <span className="text-[10px] text-gray-500">{sessions.length} active {sessions.length === 1 ? 'run' : 'runs'}</span>
        )}
      </div>

      {error ? (
        <p className="text-xs text-red-600" role="alert">Could not load the agents on this project: {error}</p>
      ) : isLoading ? (
        <p className="text-xs text-gray-400">Loading…</p>
      ) : sessions.length === 0 ? (
        <p className="text-xs text-gray-400 italic">{NO_AGENT_ON_PROJECT}</p>
      ) : (
        <ul className="divide-y divide-zinc-100 dark:divide-zinc-800">
          {sessions.map((s) => {
            const state = SESSION_STATE_STYLE[s.status];
            const note = quietNote(s);
            return (
              <li key={s.id} className="py-2 flex items-start gap-3" data-testid="project-agent-session">
                <span className={`mt-1.5 inline-flex h-2 w-2 flex-shrink-0 rounded-full ${state.dot}`} aria-hidden="true" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-xs">
                    <span className="font-semibold text-gray-800 dark:text-gray-200">{s.agentName}</span>
                    <span className="text-gray-500">{state.label}</span>
                    <span className={`ml-auto whitespace-nowrap ${note ? 'text-amber-700' : 'text-gray-500'}`}>
                      {note ?? `since ${formatSessionTime(s.startedAt)}`}
                    </span>
                  </div>
                  <div className="text-xs text-gray-700 dark:text-gray-300 truncate" title={s.task ?? undefined}>
                    {s.task ?? 'No task given'}
                  </div>
                  {s.itemId && (
                    <div className="text-[10px] text-gray-500 truncate">
                      <span className="font-mono">{s.itemId}</span>
                      {s.itemTitle && ` · ${s.itemTitle}`}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
